import agent from '@root/agent';
import { Button } from '@root/components/v3/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@root/components/v3/dialog';
import { Input } from '@root/components/v3/input';
import { Label } from '@root/components/v3/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@root/components/v3/select';
import { CommandBlock } from '@root/containers/environment/EdilEnviroment/Kubernetes/ClusterOnboarding';
import { useEnvironments } from '@root/hooks/environments';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { toast } from 'sonner';

const FREQUENCY_OPTIONS = [
  { label: 'Every day', value: '1d' },
  { label: 'Every 4 days', value: '4d' },
  { label: 'Every 8 days', value: '8d' },
  { label: 'Every 16 days', value: '16d' },
];
const ScheduleReportDialog = ({ open, onClose, clusterListData = [] }) => {
  const [selectedCluster, setSelectedCluster] = useState('');
  const [selectedFrequency, setSelectedFrequency] = useState('');
  const [bucketName, setBucketName] = useState('');
  //   const [activeStep, setActiveStep] = useState(0);
  const [copiedStates, setCopiedStates] = useState({});
  const allEnvironments = useEnvironments();
  // Derive the account_id (environment) from the selected cluster
  const selectedEnvironment = useMemo(
    () => clusterListData.find(c => c.cluster_name === selectedCluster)?.account_id || '',
    [clusterListData, selectedCluster],
  );
  const currentCloudProvider = useMemo(() => {
    if (!selectedEnvironment) return 'all';
    return allEnvironments?.filter(item => item.id === selectedEnvironment)[0].cloudProvider;
  }, [allEnvironments, selectedEnvironment]);

  // Fetch export onboarding config when a cluster is selected
  const { data: exportConfig, isFetching: isFetchingConfig } = useEnhancedQuery(
    ['kubernetes_export_config', selectedEnvironment, selectedCluster, currentCloudProvider],
    () =>
      agent.Vulnerabilites.get_k8_cve_schedule_config({
        environment: selectedEnvironment,
        cluster: selectedCluster,
        cloud_provider: currentCloudProvider,
      }),
    {
      enabled: !!selectedCluster,
    },
  );

  const onboardingData = exportConfig?.data;
  const isBucketCreated = !!onboardingData?.is_bucket_created;

  // Auto-fill suggested bucket name from API
  useEffect(() => {
    if (onboardingData?.suggested_bucket_name && !bucketName) {
      setBucketName(onboardingData.suggested_bucket_name);
    }
  }, [bucketName, onboardingData?.suggested_bucket_name]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedCluster('');
      setSelectedFrequency('');
      setBucketName('');
      //   setActiveStep(0);
    }
  }, [open]);

  const selectedClusterRegion = useMemo(
    () => clusterListData.find(c => c.cluster_name === selectedCluster)?.cluster_region || '',
    [clusterListData, selectedCluster],
  );

  // console.log(currentCloudProvider, allEnvironments, 'sdfsdfdsfsdfdsfdsf');
  // Schedule export query (triggered manually via refetch)
  const { isFetching: isSubmitting, refetch: submitScheduleExport } = useEnhancedQuery(
    [
      'kubernetes_schedule_export',
      selectedEnvironment,
      selectedCluster,
      selectedFrequency,
      bucketName,
      selectedClusterRegion,
    ],
    () =>
      agent.Vulnerabilites.schedule_k8_cve_report({
        environments: [selectedEnvironment],
        clusters: [selectedCluster],
        is_scheduled: true,
        frequency: selectedFrequency,
        export: true,
        customer_s3_config: {
          bucket: bucketName,
          region: selectedClusterRegion,
        },
      }),
    {
      enabled: false,
      onSuccess: () => {
        toast.success('CVE report scheduled successfully');
        onClose();
      },
      onError: () => {
        toast.error('Failed to schedule report. Please try again.');
      },
    },
  );

  const handleSubmit = () => {
    if (!selectedCluster) {
      toast.error('Please select a cluster');
      return;
    }
    if (!selectedFrequency) {
      toast.error('Please select a frequency');
      return;
    }
    if (!bucketName.trim()) {
      toast.error('Please enter a bucket name');
      return;
    }
    submitScheduleExport();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="min-w-[700px] max-w-[800px] p-0">
        <DialogHeader className="px-[30px] pt-[30px] pb-[15px]">
          <DialogTitle className="text-[16px] font-bold !font-[Manrope]">Schedule CVE Report</DialogTitle>
        </DialogHeader>

        <div className="px-[30px] max-h-[65vh] overflow-y-auto">
          {/* Row 1: Cluster */}

          <div className="flex  gap-[30px] justify-between">
            <div className="flex w-full gap-[30px] items-center">
              <div className="flex flex-col gap-2 flex-1">
                <div className="flex gap-1.5">
                  <Label className="text-[14px]">Cluster</Label>
                  <span className="text-red-400">*</span>
                </div>
                <Select
                  value={selectedCluster}
                  onValueChange={val => {
                    setSelectedCluster(val);
                    setBucketName('');
                    //   setActiveStep(0);
                  }}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Cluster" />
                  </SelectTrigger>
                  <SelectContent>
                    {clusterListData.map(cluster => (
                      <SelectItem key={cluster.cluster_name} value={cluster.cluster_name}>
                        {cluster.cluster_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Frequency */}
            <div className="flex w-full gap-[30px] items-center ">
              <div className="flex flex-col gap-2 flex-1">
                <div className="flex gap-1.5">
                  <Label className="text-[14px]">Frequency</Label> <span className="text-red-400">*</span>
                </div>
                <Select value={selectedFrequency} onValueChange={val => setSelectedFrequency(val)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          {/* S3 Bucket & CloudFormation Onboarding */}
          {selectedCluster && (
            <div
              style={{
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                padding: '16px',
                marginTop: '20px',
                background: 'var(--content-box-bg, #fff)',
              }}>
              <div
                style={{
                  fontWeight: '600',
                  fontSize: '14px',
                  fontFamily: 'Manrope',
                  color: '#1A202C',
                  marginBottom: '12px',
                }}>
                S3 Export Setup
              </div>

              {isFetchingConfig ? (
                <div className="flex items-center justify-center py-6">
                  <Spinner animation="border" size="sm" />
                  <span style={{ marginLeft: '8px', fontSize: '13px', color: '#718096' }}>
                    Loading configuration...
                  </span>
                </div>
              ) : isBucketCreated ? (
                <div className="flex flex-col gap-2">
                  <Label className="text-[13px] text-[#4A5568]">S3 Bucket Name</Label>
                  <Input
                    type="text"
                    value={bucketName}
                    onChange={e => setBucketName(e.target.value)}
                    placeholder="Enter S3 bucket name"
                  />
                  {onboardingData?.region && (
                    <span style={{ fontSize: '12px', color: '#718096' }}>Region: {onboardingData.region}</span>
                  )}
                </div>
              ) : (
                <>
                  <Label className="text-[13px] text-[#4A5568] mb-2">
                    Please paste and run this command in the cloudshell
                  </Label>
                  {onboardingData?.python_setup_command && (
                    <CommandBlock
                      command={onboardingData.python_setup_command}
                      commandId="python_setup"
                      isLoading={false}
                      copiedStates={copiedStates}
                      setCopiedStates={setCopiedStates}
                    />
                  )}
                </>
              )}
            </div>
          )}

          <DialogFooter className="py-[30px]">
            <DialogClose asChild>
              <Button variant={'outline'} className="text-[#1a202c]" onClick={onClose}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant={'default'}
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedCluster || !selectedFrequency || !bucketName.trim()}>
              {isSubmitting && (
                <span style={{ marginRight: '6px' }}>
                  <Spinner animation="border" size="sm" />
                </span>
              )}
              {isSubmitting ? 'Scheduling...' : 'Schedule Report'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleReportDialog;
